import { Box, Button, HStack, Input, Text, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';

import {
  hasComposerContent,
  serializeDocumentChatWireString,
  useDocumentChatComposer,
} from './document-chat-composer-context';
import { useEditDocument } from './edit-document-context';
import { usePromptSubmit } from './prompt-command';

import {
  usePageFooter,
  usePageLayout,
} from '#src/providers/page-layout-provider';

export interface DocumentChatFooterProps {
  documentId: string | undefined;
  isNewDocument: boolean;
}

function tagLabel(payload: Record<string, unknown>): string {
  const sid = payload.sectionId;
  if (typeof sid === 'string' && sid.trim()) {
    return `Section ${sid.length > 24 ? `${sid.slice(0, 22)}…` : sid}`;
  }
  const raw = JSON.stringify(payload);
  return raw.length > 40 ? `${raw.slice(0, 38)}…` : raw;
}

/**
 * Document AI chat: portals into the app shell footer ([`PageFooter`](../../components/page-footer.tsx))
 * like [`Dashboard`](../../pages/dashboard.tsx), using `usePageFooter` + `createPortal`.
 */
export function DocumentChatFooter({
  documentId,
  isNewDocument,
}: DocumentChatFooterProps) {
  const { footerRef } = usePageFooter();
  const { setFooterMode } = usePageLayout();
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setFooterMode('fixed');

    let cancelled = false;
    const tryAttach = (attempt: number) => {
      if (cancelled) return;
      if (footerRef.current) {
        setPortalTarget(footerRef.current);
        return;
      }
      if (attempt > 50) return;
      setTimeout(() => tryAttach(attempt + 1), 0);
    };
    tryAttach(0);

    return () => {
      cancelled = true;
      setFooterMode('float');
      setPortalTarget(null);
    };
  }, [footerRef, setFooterMode]);

  const { setValue } = useEditDocument();
  const { segments, updateTextSegment, removeSegment, clear, composerRootRef } =
    useDocumentChatComposer();
  const { handleSubmit, disabled, processingPrompt } = usePromptSubmit({
    documentId,
    isNewDocument,
    focusBlockId: undefined,
    onValueUpdate: setValue,
  });

  const onApply = useCallback(async () => {
    if (!hasComposerContent(segments)) return;
    const wire = serializeDocumentChatWireString(segments).trim();
    if (!wire) return;
    const ok = await handleSubmit(wire);
    if (ok) clear();
  }, [segments, clear, handleSubmit]);

  const canSubmit = hasComposerContent(segments) && !disabled;

  const footerContent = (
    <VStack gap={2} align="stretch" width="100%">
      <Box
        ref={composerRootRef}
        borderWidth="1px"
        borderColor="border"
        borderRadius="md"
        px={2}
        py={2}
        minH="44px"
        bg="bg.subtle"
      >
        <HStack gap={2} align="center" flexWrap="wrap" alignItems="center">
          {segments.map((seg) =>
            seg.type === 'text' ? (
              <Input
                key={seg.id}
                data-chat-composer-text="true"
                size="sm"
                variant="flushed"
                flex="1 1 140px"
                minW="120px"
                border="none"
                _focusVisible={{ boxShadow: 'none' }}
                value={seg.value}
                onChange={(e) => updateTextSegment(seg.id, e.target.value)}
                placeholder="Describe the change…"
                disabled={disabled}
              />
            ) : (
              <HStack
                key={seg.id}
                gap={0}
                align="center"
                borderRadius="md"
                bg="bg.muted"
                borderWidth="1px"
                borderColor="border"
                pl={2}
                pr={0}
                maxW="100%"
              >
                <Text
                  fontSize="xs"
                  fontFamily="mono"
                  color="fg.muted"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  maxW="min(280px, 100vw - 120px)"
                >
                  {tagLabel(seg.payload)}
                </Text>
                <Button
                  type="button"
                  size="xs"
                  variant="ghost"
                  aria-label="Remove tag"
                  disabled={disabled}
                  onClick={() => removeSegment(seg.id)}
                >
                  <FiX />
                </Button>
              </HStack>
            ),
          )}
        </HStack>
      </Box>
      <HStack justify="flex-end">
        <Button
          size="sm"
          colorScheme="purple"
          onClick={onApply}
          disabled={!canSubmit}
          loading={processingPrompt}
          loadingText="Applying…"
        >
          Apply
        </Button>
      </HStack>
    </VStack>
  );

  if (!portalTarget) return null;

  return createPortal(footerContent, portalTarget);
}
